import { flash, SubmitButton } from "@xola/ui-kit";
import { isScheduled } from "common";
import { pick } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { EquipmentAllocationStrategy, Experience, GuidePreference, Permission } from "types";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { IsGranted } from "../../Common/IsGranted";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { ExperienceEquipmentFields } from "./ExperienceEquipmentFields";
import { ExperienceGuideFields } from "./ExperienceGuideFields";
import { ExperienceResourceSettingValues, ExperienceResourceValidationSchema } from "./ExperienceResourceTypes";
import { AssignmentStrategyWarningInfoModal } from "@/components/Resources/EquipmentManagement/GlobalSettings";
import { sanitizeExperiencePayload } from "@/models/Experience";

export const ExperienceResourcesSettings = ({ experience }: { experience: Experience }) => {
    const invalidate = useInvalidate();
    const [showConfirmWarning, setShowConfirmWarning] = useState<boolean>(false);
    const [customOrderChanged, setCustomOrderChanged] = useState(false);

    const { mutateAsync: saveGuidePreferences } = useMutation(async (payload: GuidePreference) => {
        return api.post(
            `/experiences/${experience.id}/guide`,
            pick(payload, ["includeGuideInfo", "referGuideAs", "guideNotAssigned"]),
        );
    });

    const { mutateAsync: saveEquipmentPreferences } = useMutation(
        async (
            payload: Pick<ExperienceResourceSettingValues, "duration" | "earlyReturn" | "allowCheckoutAtArrival">,
        ) => {
            return api.put(`/experiences/${experience.id}`, sanitizeExperiencePayload(payload));
        },
    );

    const { mutateAsync: saveEquipmentAssignmentPreferences } = useMutation(
        async (payload: ExperienceResourceSettingValues["inventory"]) => {
            if (payload && payload.allocationStrategy !== EquipmentAllocationStrategy.CUSTOM_ASSIGNMENT) {
                delete payload.prioritizedResources;
            }

            return api.post(`/experiences/${experience.id}/inventory`, payload);
        },
    );

    const { mutateAsync: deleteEquipmentAssignmentPreferences } = useMutation(async () => {
        return api.delete(`/experiences/${experience.id}/inventory`);
    });

    const onSave = async (values: ExperienceResourceSettingValues) => {
        const isOverrideDisabled = initialValues.inventory && values.inventory === undefined;

        await Promise.all([
            values.guidePreference ? saveGuidePreferences(values.guidePreference) : null,
            saveEquipmentPreferences(pick(values, ["duration", "earlyReturn", "allowCheckoutAtArrival"])),
            values.inventory && saveEquipmentAssignmentPreferences(values.inventory),
            isOverrideDisabled && deleteEquipmentAssignmentPreferences(),
        ]);
        invalidate("experiences", experience.id);
        flash.success("Successfully updated preferences");
    };

    const initialValues: ExperienceResourceSettingValues = pick(experience, [
        "guidePreference",
        "earlyReturn",
        "allowCheckoutAtArrival",
        "eventDuration",
        "duration",
        "inventory",
    ]);

    return (
        <div className="flex flex-col p-8">
            <h2>Resources</h2>
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={ExperienceResourceValidationSchema}
                initialValues={initialValues}
                onSubmit={onSave}
            >
                {({ errors, isSubmitting, handleSubmit, handleReset, values }: any) => {
                    const isAllocationStrategyChanged =
                        initialValues.inventory?.allocationStrategy !== values.inventory?.allocationStrategy;
                    const needConfirmation = isAllocationStrategyChanged || customOrderChanged;

                    return (
                        <>
                            <div className="divide-y divide-gray-light">
                                <IsGranted permissions={Permission.MODIFY_RESOURCES_GUIDE}>
                                    <ExperienceGuideFields />
                                </IsGranted>
                                <IsGranted permissions={Permission.MODIFY_EXPERIENCE_RESOURCES}>
                                    {isScheduled(experience) ? (
                                        <ExperienceEquipmentFields
                                            experience={experience}
                                            setCustomOrderChanged={setCustomOrderChanged}
                                        />
                                    ) : null}
                                </IsGranted>
                            </div>

                            <AssignmentStrategyWarningInfoModal
                                isOpen={showConfirmWarning}
                                isCustomOrderChanged={!isAllocationStrategyChanged && customOrderChanged}
                                handleSave={() => {
                                    setShowConfirmWarning(false);
                                    handleSubmit();
                                }}
                                setConfirm={setShowConfirmWarning}
                            />

                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton
                                    isLoading={isSubmitting}
                                    disabled={errors.length > 0}
                                    onClick={needConfirmation ? () => setShowConfirmWarning(true) : handleSubmit}
                                >
                                    Save
                                </SubmitButton>

                                <SecondaryButton type="reset" onClick={handleReset}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </>
                    );
                }}
            </XolaFormik>
        </div>
    );
};
